/**
 * Floating button that opens the AI surface for the CURRENT ROLE.
 *
 * Named for what it does, not for either product it can open. It was
 * `AICoachFAB`, which was accurate while founder coaching was the only AI
 * surface and misleading once the investor assistant existed — it is used by
 * both, and the two are different products with different backends
 * (`startup-insights` coaching vs `useDDChat` deal analysis).
 *
 * `flagKey` is required precisely because of that: each product has its own
 * kill-switch and neither should be able to inherit the other's by omission.
 */
import { Pressable, Text } from 'react-native';
import { useRouter, type Href } from 'expo-router';
import { LinearGradient } from 'expo-linear-gradient';
import { Sparkles } from 'lucide-react-native';
import Animated, { FadeInUp, useSharedValue, useAnimatedStyle, withSpring } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { hapticMedium } from '@/lib/haptics';
import { useFlag } from '@/lib/feature-flags';

interface AILauncherFABProps {
  targetRoute: string;
  /**
   * Which feature this FAB belongs to. REQUIRED, deliberately.
   *
   * The startup coach and the investor assistant are different products with
   * different backends — founder coaching (`startup-insights`) versus deal
   * analysis (`useDDChat`) — and must be pausable independently. They were
   * briefly gated on one shared key, which meant un-pausing founder coaching
   * would also have exposed the investor assistant. Making this required means
   * a new FAB cannot silently inherit the wrong product's switch.
   */
  flagKey: 'ai_coach' | 'ai_assistant';
  bottom?: number;
}

export default function AILauncherFAB({ targetRoute, flagKey, bottom = 24 }: AILauncherFABProps) {
  const router = useRouter();
  const colors = useThemeColors();
  const scale = useSharedValue(1);

  // Paused for beta/launch — hidden until further notice. Kill-switch style:
  // default OFF, re-enabled from the admin flags screen with no redeploy.
  // Gating here hides every usage of that product's FAB in one place.
  // Hooks above must run before this return.
  const enabled = useFlag(flagKey, { default: false });

  const animStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  if (!enabled) return null;

  return (
    <Animated.View
      entering={FadeInUp.delay(600).duration(500)}
      style={{
        position: 'absolute',
        bottom,
        right: 20,
        zIndex: 50,
      }}
    >
      <Pressable
        onPressIn={() => {
          scale.value = withSpring(0.92, { damping: 15, stiffness: 300 });
        }}
        onPressOut={() => {
          scale.value = withSpring(1, { damping: 12, stiffness: 200 });
        }}
        onPress={() => {
          hapticMedium();
          router.push(targetRoute as Href);
        }}
      >
        <Animated.View style={animStyle}>
          <LinearGradient
            colors={[colors.primary[400], colors.primary[600]]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 6,
              paddingHorizontal: 16,
              paddingVertical: 12,
              borderRadius: 24,
              shadowColor: colors.primary[500],
              shadowOpacity: 0.4,
              shadowRadius: 12,
              shadowOffset: { width: 0, height: 6 },
              elevation: 8,
            }}
          >
            <Sparkles size={18} color="#fff" />
            <Text style={{ color: '#fff', fontSize: 14, fontWeight: '700' }}>AI Coach</Text>
          </LinearGradient>
        </Animated.View>
      </Pressable>
    </Animated.View>
  );
}
